import { Button, Textarea } from "@repo/ui";
import { Badge } from "@repo/ui";
import { Forward, Send, X } from "lucide-react";
import { cn } from "@repo/utils";
import type { TypeReponseMail } from "../../templates/types";
import { InfoTooltip } from "./InfoTooltip";

interface MailComposeFormProps {
  composeMode: "reply" | "forward";
  reponseText: string;
  transferRecipients: string;
  typeReponse: TypeReponseMail;
  emetteur: string;
  originalResponse: string | null;
  isEditMode?: boolean;
  onReponseChange: (v: string) => void;
  onTransferRecipientsChange: (v: string) => void;
  onSend: () => void;
  onClose: () => void;
}

export function MailComposeForm({
  composeMode,
  reponseText,
  transferRecipients,
  typeReponse,
  emetteur,
  originalResponse,
  isEditMode = false,
  onReponseChange,
  onTransferRecipientsChange,
  onSend,
  onClose,
}: MailComposeFormProps) {
  return (
    <div
      className={cn(
        "rounded-surface border p-4",
        isEditMode
          ? "bg-card border-border"
          : composeMode === "forward"
            ? "bg-blue-50 border-blue-200"
            : "bg-card border-border",
      )}
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isEditMode ? (
              <span className="text-sm font-medium text-muted-foreground">
                {typeReponse === "list"
                  ? "Modifier mes choix"
                  : `Modifier ma réponse à ${emetteur}`}
              </span>
            ) : composeMode === "forward" ? (
              <>
                <Forward className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">
                  Transférer ce message
                </span>
                <Badge variant="info" className="text-xs">
                  Transfert
                </Badge>
              </>
            ) : typeReponse === "list" ? (
              <>
                <span className="text-sm font-medium text-muted-foreground">
                  Expliquer mes choix
                </span>
                <InfoTooltip />
              </>
            ) : (
              <span className="text-sm font-medium text-muted-foreground">
                Répondre à {emetteur}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onClose}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>

        {composeMode === "forward" && !isEditMode && (
          <div className="flex items-center gap-2 rounded-surface border border-blue-200 bg-white px-3 py-2">
            <span className="text-sm text-muted-foreground">À :</span>
            <input
              type="text"
              value={transferRecipients}
              onChange={(e) => onTransferRecipientsChange(e.target.value)}
              placeholder="destinataire1, destinataire2, ..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
        )}

        <Textarea
          value={reponseText}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onReponseChange(e.target.value)
          }
          placeholder={
            composeMode === "forward"
              ? "Ajouter un message (optionnel)..."
              : typeReponse === "list"
                ? "Listez les tâches à effectuer..."
                : "Votre réponse..."
          }
          className={cn(
            "min-h-25 resize-none",
            composeMode === "forward" &&
              !isEditMode &&
              "border-blue-200 focus-visible:ring-blue-500",
          )}
        />
        <div className="flex items-center justify-end">
          <Button
            size="sm"
            onClick={onSend}
            disabled={composeMode === "reply" && !reponseText.trim()}
            className={cn(
              composeMode === "forward" &&
                !isEditMode &&
                "bg-blue-600 hover:bg-blue-700",
            )}
          >
            <Send className="mr-2 h-4 w-4" />
            {composeMode === "forward" && !isEditMode
              ? "Transférer"
              : "Envoyer"}
          </Button>
        </div>
      </div>
    </div>
  );
}
